import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';

interface ScoreRingProps {
  /** Value between 0 and 1 (0.85 = 85%) */
  value: number;
  size?: 'sm' | 'md' | 'lg';
  /** 'auto' picks green/amber/red based on thresholds */
  color?: 'green' | 'amber' | 'red' | 'auto';
  showLabel?: boolean;
}

const SIZES: Record<string, number> = { sm: 32, md: 48, lg: 96 };
const INSET: Record<string, number> = { sm: 4, md: 6, lg: 8 };
const FONT: Record<string, string> = { sm: 'text-[8px]', md: 'text-xs', lg: 'text-2xl' };
const COLOR_HEX: Record<string, string> = { green: '#22c55e', amber: '#f59e0b', red: '#ef4444' };
const COLOR_TEXT: Record<string, string> = { green: 'text-green-400', amber: 'text-amber-400', red: 'text-red-400' };

function autoColor(value: number): string {
  return value >= 0.7 ? 'green' : value >= 0.4 ? 'amber' : 'red';
}

const ScoreRing: React.FC<ScoreRingProps> = ({ value, size = 'md', color = 'auto', showLabel = true }) => {
  const { darkMode } = useTheme();
  const resolved = color === 'auto' ? autoColor(value) : color;
  const outer = SIZES[size];
  const inner = outer - INSET[size] * 2;
  const pct = Math.round(Math.max(0, Math.min(1, value)) * 100);

  return (
    <div
      className="inline-flex items-center justify-center rounded-full shrink-0"
      style={{
        width: outer,
        height: outer,
        background: `conic-gradient(${COLOR_HEX[resolved]} ${pct}%, ${darkMode ? '#374151' : '#e5e7eb'} 0)`,
      }}
    >
      <div
        className={`rounded-full flex items-center justify-center ${darkMode ? 'bg-gray-900' : 'bg-white'}`}
        style={{ width: inner, height: inner }}
      >
        {showLabel && (
          <span className={`font-bold ${FONT[size]} ${COLOR_TEXT[resolved]}`}>{pct}%</span>
        )}
      </div>
    </div>
  );
};

export default ScoreRing;
